import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  Link,
  useLocation,
  Navigate,
  useParams,
  useNavigate,
} from "react-router-dom";
import axios from "axios";
import { Icon } from "./Icon";

interface KnowledgeEntry {
  id: string;
  name: string;
  description: string;
  tags: string[];
  isActive: boolean;
  usageCount: number;
  lastUsedAt: string | null;
  attributionLevel: "none" | "subtle" | "full";
  createdAt: string;
  updatedAt: string;
}

interface KnowledgeBaseManagerProps {
  authToken: string;
  userType?: "user" | "premium" | "vip" | "admin";
}

const KnowledgeBaseManager: React.FC<KnowledgeBaseManagerProps> = ({
  authToken,
  userType = "user",
}) => {
  const location = useLocation();
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  // Determine entry limits based on user type
  const isPremium =
    userType === "premium" || userType === "vip" || userType === "admin";
  const maxEntries = isPremium ? 100 : 3;

  // Check if we're on a sub-route
  const isSubRoute = location.pathname.includes(
    "/dashboard/memorybank/knowledge/",
  );
  const pathSegments = location.pathname.split("/");
  const lastSegment = pathSegments[pathSegments.length - 1];

  // Determine active tab - if last segment looks like UUID, we're on edit page
  const isUUID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      lastSegment,
    );
  const activeSubTab = isUUID ? "edit" : lastSegment || "items";

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (activeSubTab === "items") {
      fetchEntries();
      fetchTags();
    } else if (activeSubTab === "add") {
      fetchTags();
    }
  }, [activeSubTab]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/bot/knowledge", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setEntries(response.data);
    } catch (error) {
      console.error("Error fetching knowledge entries:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get("/api/bot/knowledge/tags", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setAllTags(response.data);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  // Filter entries based on search and filters
  const filteredEntries = entries.filter((entry) => {
    const matchesSearch =
      activeSearchQuery === "" ||
      entry.name.toLowerCase().includes(activeSearchQuery.toLowerCase()) ||
      entry.description.toLowerCase().includes(activeSearchQuery.toLowerCase());

    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.some((tag) => entry.tags.includes(tag));

    return matchesSearch && matchesTags;
  });

  const handleSearch = () => {
    setActiveSearchQuery(searchQuery);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Sub-navigation style
  const subNavStyle = {
    display: "flex",
    gap: "20px",
    marginBottom: "24px",
    borderBottom: "1px solid #4a5568",
    paddingBottom: "12px",
  };

  const subNavLinkStyle = (isActive: boolean) => ({
    color: isActive ? "#9146FF" : "#cbd5e0",
    textDecoration: "none",
    fontSize: "16px",
    fontWeight: isActive ? "600" : "400",
    paddingBottom: "12px",
    borderBottom: isActive ? "2px solid #9146FF" : "2px solid transparent",
    transition: "all 0.2s",
  });

  // Knowledge Items Component
  const KnowledgeItems = React.memo(function KnowledgeItems() {
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

    const handleLocalSearch = () => {
      setSearchQuery(localSearchQuery);
      setActiveSearchQuery(localSearchQuery);
    };

    const handleLocalKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleLocalSearch();
      }
    };

    return (
      <div>
        {/* Search and Filters */}
        <div
          style={{
            marginBottom: "24px",
            display: "flex",
            gap: "16px",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <input
            type="text"
            placeholder="Search knowledge base..."
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            onKeyPress={handleLocalKeyPress}
            style={{
              flex: 1,
              minWidth: isMobile ? "100%" : "200px",
              padding: "10px 16px",
              backgroundColor: "#2d3748",
              border: "1px solid #4a5568",
              borderRadius: "8px",
              color: "#e2e8f0",
              fontSize: "14px",
            }}
          />

          <button
            onClick={handleLocalSearch}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4a5568",
              color: "#e2e8f0",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            Search
          </button>
        </div>

        {/* Entries List */}
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#a0aec0" }}
          >
            Loading knowledge base...
          </div>
        ) : filteredEntries.length === 0 ? (
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "40px",
              borderRadius: "8px",
              textAlign: "center",
              color: "#a0aec0",
            }}
          >
            {entries.length === 0
              ? "No knowledge entries yet. Click 'Add Entry' to create your first one!"
              : "No entries match your search criteria."}
          </div>
        ) : (
          <div style={{ display: "grid", gap: "16px" }}>
            {filteredEntries.map((entry) => (
              <div
                key={entry.id}
                style={{
                  backgroundColor: "#2d3748",
                  padding: "20px",
                  borderRadius: "8px",
                  border: "1px solid #4a5568",
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "12px",
                  }}
                >
                  <div>
                    <h4
                      style={{
                        margin: 0,
                        color: "#e2e8f0",
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      {entry.name}
                    </h4>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      style={{
                        padding: "4px 12px",
                        backgroundColor: entry.isActive ? "#065F46" : "#7C2D12",
                        color: entry.isActive ? "#10B981" : "#F87171",
                        borderRadius: "20px",
                        fontSize: "12px",
                        marginBottom: "4px",
                      }}
                    >
                      {entry.isActive ? "Active" : "Inactive"}
                    </div>
                    {entry.usageCount > 0 && (
                      <div style={{ fontSize: "12px", color: "#cbd5e0" }}>
                        Used {entry.usageCount} times
                      </div>
                    )}
                  </div>
                </div>

                <p
                  style={{
                    color: "#cbd5e0",
                    margin: "0 0 12px 0",
                    fontSize: "14px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {entry.description}
                </p>

                {entry.tags.length > 0 && (
                  <div
                    style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}
                  >
                    {entry.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: "11px",
                          padding: "2px 8px",
                          backgroundColor: "#374151",
                          color: "#cbd5e0",
                          borderRadius: "12px",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Bottom section with last updated and edit button */}
                <div
                  style={{
                    marginTop: "16px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  {/* Last updated date */}
                  <div style={{ fontSize: "12px", color: "#718096" }}>
                    Last updated:{" "}
                    {new Date(entry.updatedAt).toLocaleDateString()} at{" "}
                    {new Date(entry.updatedAt).toLocaleTimeString()}
                  </div>
                  <button
                    onClick={() => {
                      window.location.href = `/dashboard/memorybank/knowledge/edit/${entry.id}`;
                    }}
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#4a5568",
                      color: "#e2e8f0",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "14px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#9146FF";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#4a5568";
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Edit Entry
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  });

  // Add Knowledge Entry Component
  const AddKnowledgeEntry = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
      name: "",
      description: "",
      tags: [] as string[],
      isActive: true,
      attributionLevel: "none" as "none" | "subtle" | "full",
    });
    const [tagInput, setTagInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [showTagSuggestions, setShowTagSuggestions] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // Redirect if at limit
    useEffect(() => {
      if (entries.length >= maxEntries) {
        if (isPremium) {
          alert(
            `You have reached the maximum limit of ${maxEntries} knowledge entries.`,
          );
        } else {
          alert(
            `Free users can only create up to ${maxEntries} knowledge entries. Upgrade to Premium for up to 100 entries!`,
          );
        }
        navigate("/dashboard/memorybank/knowledge/items");
      }
    }, [entries.length, maxEntries, isPremium, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      // Check if user has reached their entry limit
      if (entries.length >= maxEntries) {
        if (isPremium) {
          alert(
            `You have reached the maximum limit of ${maxEntries} knowledge entries.`,
          );
        } else {
          alert(
            `Free users can only create up to ${maxEntries} knowledge entries. Upgrade to Premium for up to 100 entries!`,
          );
        }
        return;
      }

      setSaving(true);

      try {
        await axios.post("/api/bot/knowledge", formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        // Reset form
        setFormData({
          name: "",
          description: "",
          tags: [],
          isActive: true,
          attributionLevel: "none",
        });

        // Refresh entries and tags
        await fetchEntries();
        await fetchTags();

        // Navigate to items tab
        window.location.href = "/dashboard/memorybank/knowledge/items";
      } catch (error) {
        console.error("Error saving knowledge entry:", error);
        alert("Failed to save knowledge entry. Please try again.");
      } finally {
        setSaving(false);
      }
    };

    const handleAddTag = (tag: string) => {
      const trimmedTag = tag.trim();
      if (trimmedTag && !formData.tags.includes(trimmedTag)) {
        if (formData.tags.length >= 10) {
          alert("Maximum 10 tags allowed");
          return;
        }
        if (trimmedTag.length > 30) {
          alert("Each tag must be 30 characters or less");
          return;
        }
        setFormData({ ...formData, tags: [...formData.tags, trimmedTag] });
      }
      setTagInput("");
      setShowTagSuggestions(false);
    };

    const handleRemoveTag = (tagToRemove: string) => {
      setFormData({
        ...formData,
        tags: formData.tags.filter((tag) => tag !== tagToRemove),
      });
    };

    const filteredTagSuggestions = allTags.filter(
      (tag) =>
        tag.toLowerCase().includes(tagInput.toLowerCase()) &&
        !formData.tags.includes(tag),
    );

    return (
      <div>
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "24px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              marginBottom: "20px",
              color: "#e2e8f0",
            }}
          >
            Create New Knowledge Entry
          </h3>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#cbd5e0",
                  marginBottom: "8px",
                }}
              >
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                maxLength={100}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  backgroundColor: "#1a202c",
                  border: "1px solid #4a5568",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
                placeholder="e.g., Stream Schedule, Bot Commands"
              />
              <div
                style={{
                  fontSize: "12px",
                  color: formData.name.length > 90 ? "#F59E0B" : "#718096",
                  marginTop: "4px",
                  textAlign: "right",
                }}
              >
                {formData.name.length}/100 characters
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#cbd5e0",
                  marginBottom: "8px",
                }}
              >
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                required
                maxLength={2000}
                rows={6}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  backgroundColor: "#1a202c",
                  border: "1px solid #4a5568",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
                placeholder="Detailed information that the bot should know..."
              />
              <div
                style={{
                  fontSize: "12px",
                  color:
                    formData.description.length > 1800 ? "#F59E0B" : "#718096",
                  marginTop: "4px",
                  textAlign: "right",
                }}
              >
                {formData.description.length}/2000 characters
              </div>
            </div>

            <div style={{ marginBottom: "20px", position: "relative" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#cbd5e0",
                  marginBottom: "8px",
                }}
              >
                Tags{" "}
                {formData.tags.length > 0 && `(${formData.tags.length}/10)`}
              </label>
              <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => {
                    setTagInput(e.target.value);
                    setShowTagSuggestions(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag(tagInput);
                    }
                  }}
                  onFocus={() => setShowTagSuggestions(true)}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    backgroundColor: "#1a202c",
                    border: "1px solid #4a5568",
                    borderRadius: "8px",
                    color: "#e2e8f0",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                  placeholder="Add tags..."
                />
                <button
                  type="button"
                  onClick={() => handleAddTag(tagInput)}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#4a5568",
                    color: "#e2e8f0",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Add
                </button>
              </div>

              {/* Tag suggestions dropdown */}
              {showTagSuggestions &&
                tagInput &&
                filteredTagSuggestions.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "#1a202c",
                      border: "1px solid #4a5568",
                      borderRadius: "8px",
                      marginTop: "4px",
                      maxHeight: "150px",
                      overflowY: "auto",
                      zIndex: 10,
                    }}
                  >
                    {filteredTagSuggestions.map((tag) => (
                      <div
                        key={tag}
                        onClick={() => handleAddTag(tag)}
                        style={{
                          padding: "8px 16px",
                          cursor: "pointer",
                          color: "#cbd5e0",
                          fontSize: "14px",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#2d3748";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                )}

              {/* Selected tags */}
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      padding: "4px 12px",
                      backgroundColor: "#374151",
                      color: "#cbd5e0",
                      borderRadius: "16px",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#9ca3af",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              {/* Existing tags to choose from */}
              {allTags.length > 0 && (
                <div style={{ marginTop: "12px" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#a0aec0",
                      marginBottom: "8px",
                    }}
                  >
                    Available tags:
                  </div>
                  <div
                    style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}
                  >
                    {allTags
                      .filter((tag) => !formData.tags.includes(tag))
                      .map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => handleAddTag(tag)}
                          style={{
                            padding: "4px 12px",
                            backgroundColor: "#1a202c",
                            color: "#cbd5e0",
                            border: "1px solid #4a5568",
                            borderRadius: "16px",
                            fontSize: "12px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#374151";
                            e.currentTarget.style.borderColor = "#9146FF";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#1a202c";
                            e.currentTarget.style.borderColor = "#4a5568";
                          }}
                        >
                          {tag}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#cbd5e0",
                  marginBottom: "8px",
                }}
              >
                Attribution Level
              </label>
              <select
                value={formData.attributionLevel}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    attributionLevel: e.target.value as any,
                  })
                }
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  backgroundColor: "#1a202c",
                  border: "1px solid #4a5568",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23cbd5e0' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: "20px",
                  paddingRight: "40px",
                  boxSizing: "border-box",
                }}
              >
                <option value="none">None - No attribution</option>
                <option value="subtle">Subtle - Mention knowledge base</option>
                <option value="full">Full - Cite specific entry name</option>
              </select>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  style={{
                    width: "18px",
                    height: "18px",
                    cursor: "pointer",
                  }}
                />
                Active (Bot will use this entry when responding)
              </label>
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: "10px 24px",
                  backgroundColor: "#9146FF",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.5 : 1,
                }}
              >
                {saving ? "Creating..." : "Create Entry"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Edit Knowledge Entry Component
  const EditKnowledgeEntry = () => {
    const { id } = useParams<{ id: string }>();
    const [formData, setFormData] = useState({
      name: "",
      description: "",
      tags: [] as string[],
      isActive: true,
      attributionLevel: "none" as "none" | "subtle" | "full",
    });
    const [tagInput, setTagInput] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showTagSuggestions, setShowTagSuggestions] = useState(false);
    const [allTags, setAllTags] = useState<string[]>([]);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    useEffect(() => {
      if (!initialLoadDone && id) {
        fetchEntry();
        fetchTags();
        setInitialLoadDone(true);
      }
    }, [id, initialLoadDone]); // Only run once when we have an ID

    const fetchEntry = async () => {
      try {
        const response = await axios.get(`/api/bot/knowledge/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const entry = response.data;
        setFormData({
          name: entry.name,
          description: entry.description,
          tags: entry.tags,
          isActive: entry.isActive,
          attributionLevel: entry.attributionLevel,
        });
      } catch (error) {
        console.error("Error fetching knowledge entry:", error);
        alert("Failed to load knowledge entry.");
        window.location.href = "/dashboard/memorybank/knowledge/items";
      } finally {
        setLoading(false);
      }
    };

    const fetchTags = async () => {
      try {
        const response = await axios.get("/api/bot/knowledge/tags", {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setAllTags(response.data);
      } catch (error) {
        console.error("Error fetching tags:", error);
      }
    };

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setSaving(true);

      try {
        await axios.put(`/api/bot/knowledge/${id}`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        // Navigate back to items
        window.location.href = "/dashboard/memorybank/knowledge/items";
      } catch (error) {
        console.error("Error updating knowledge entry:", error);
        alert("Failed to update knowledge entry. Please try again.");
      } finally {
        setSaving(false);
      }
    };

    const handleDelete = async () => {
      if (
        !window.confirm("Are you sure you want to delete this knowledge entry?")
      ) {
        return;
      }

      try {
        await axios.delete(`/api/bot/knowledge/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        window.location.href = "/dashboard/memorybank/knowledge/items";
      } catch (error) {
        console.error("Error deleting knowledge entry:", error);
        alert("Failed to delete knowledge entry.");
      }
    };

    const handleAddTag = (tag: string) => {
      const trimmedTag = tag.trim();
      if (trimmedTag && !formData.tags.includes(trimmedTag)) {
        if (formData.tags.length >= 10) {
          alert("Maximum 10 tags allowed");
          return;
        }
        if (trimmedTag.length > 30) {
          alert("Each tag must be 30 characters or less");
          return;
        }
        setFormData({ ...formData, tags: [...formData.tags, trimmedTag] });
      }
      setTagInput("");
      setShowTagSuggestions(false);
    };

    const handleRemoveTag = (tagToRemove: string) => {
      setFormData({
        ...formData,
        tags: formData.tags.filter((tag) => tag !== tagToRemove),
      });
    };

    const filteredTagSuggestions = allTags.filter(
      (tag) =>
        tag.toLowerCase().includes(tagInput.toLowerCase()) &&
        !formData.tags.includes(tag),
    );

    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "40px", color: "#a0aec0" }}>
          Loading knowledge entry...
        </div>
      );
    }

    return (
      <div>
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "24px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                margin: 0,
                color: "#e2e8f0",
              }}
            >
              Edit Knowledge Entry
            </h3>
            <button
              onClick={handleDelete}
              style={{
                padding: "8px 16px",
                backgroundColor: "#E53E3E",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Delete Entry
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#cbd5e0",
                  marginBottom: "8px",
                }}
              >
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                maxLength={100}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  backgroundColor: "#1a202c",
                  border: "1px solid #4a5568",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
                placeholder="e.g., Stream Schedule, Bot Commands"
              />
              <div
                style={{
                  fontSize: "12px",
                  color: formData.name.length > 90 ? "#F59E0B" : "#718096",
                  marginTop: "4px",
                  textAlign: "right",
                }}
              >
                {formData.name.length}/100 characters
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#cbd5e0",
                  marginBottom: "8px",
                }}
              >
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                required
                maxLength={2000}
                rows={6}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  backgroundColor: "#1a202c",
                  border: "1px solid #4a5568",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
                placeholder="Detailed information that the bot should know..."
              />
              <div
                style={{
                  fontSize: "12px",
                  color:
                    formData.description.length > 1800 ? "#F59E0B" : "#718096",
                  marginTop: "4px",
                  textAlign: "right",
                }}
              >
                {formData.description.length}/2000 characters
              </div>
            </div>

            <div style={{ marginBottom: "20px", position: "relative" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#cbd5e0",
                  marginBottom: "8px",
                }}
              >
                Tags{" "}
                {formData.tags.length > 0 && `(${formData.tags.length}/10)`}
              </label>
              <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => {
                    setTagInput(e.target.value);
                    setShowTagSuggestions(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddTag(tagInput);
                    }
                  }}
                  onFocus={() => setShowTagSuggestions(true)}
                  style={{
                    flex: 1,
                    padding: "10px 16px",
                    backgroundColor: "#1a202c",
                    border: "1px solid #4a5568",
                    borderRadius: "8px",
                    color: "#e2e8f0",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                  placeholder="Add tags..."
                />
                <button
                  type="button"
                  onClick={() => handleAddTag(tagInput)}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#4a5568",
                    color: "#e2e8f0",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Add
                </button>
              </div>

              {/* Tag suggestions dropdown */}
              {showTagSuggestions &&
                tagInput &&
                filteredTagSuggestions.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "#1a202c",
                      border: "1px solid #4a5568",
                      borderRadius: "8px",
                      marginTop: "4px",
                      maxHeight: "150px",
                      overflowY: "auto",
                      zIndex: 10,
                    }}
                  >
                    {filteredTagSuggestions.map((tag) => (
                      <div
                        key={tag}
                        onClick={() => handleAddTag(tag)}
                        style={{
                          padding: "8px 16px",
                          cursor: "pointer",
                          color: "#cbd5e0",
                          fontSize: "14px",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#2d3748";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                )}

              {/* Selected tags */}
              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      padding: "4px 12px",
                      backgroundColor: "#374151",
                      color: "#cbd5e0",
                      borderRadius: "16px",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#9ca3af",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: "16px",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              {/* Existing tags to choose from */}
              {allTags.length > 0 && (
                <div style={{ marginTop: "12px" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#a0aec0",
                      marginBottom: "8px",
                    }}
                  >
                    Available tags:
                  </div>
                  <div
                    style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}
                  >
                    {allTags
                      .filter((tag) => !formData.tags.includes(tag))
                      .map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => handleAddTag(tag)}
                          style={{
                            padding: "4px 12px",
                            backgroundColor: "#1a202c",
                            color: "#cbd5e0",
                            border: "1px solid #4a5568",
                            borderRadius: "16px",
                            fontSize: "12px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#374151";
                            e.currentTarget.style.borderColor = "#9146FF";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#1a202c";
                            e.currentTarget.style.borderColor = "#4a5568";
                          }}
                        >
                          {tag}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#cbd5e0",
                  marginBottom: "8px",
                }}
              >
                Attribution Level
              </label>
              <select
                value={formData.attributionLevel}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    attributionLevel: e.target.value as any,
                  })
                }
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  backgroundColor: "#1a202c",
                  border: "1px solid #4a5568",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "14px",
                  appearance: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23cbd5e0' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  backgroundSize: "20px",
                  paddingRight: "40px",
                  boxSizing: "border-box",
                }}
              >
                <option value="none">None - No attribution</option>
                <option value="subtle">Subtle - Mention knowledge base</option>
                <option value="full">Full - Cite specific entry name</option>
              </select>
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  color: "#cbd5e0",
                  fontSize: "14px",
                }}
              >
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  style={{
                    width: "18px",
                    height: "18px",
                    cursor: "pointer",
                  }}
                />
                Active (Bot will use this entry when responding)
              </label>
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                type="button"
                onClick={() =>
                  (window.location.href =
                    "/dashboard/memorybank/knowledge/items")
                }
                style={{
                  padding: "10px 24px",
                  backgroundColor: "#4a5568",
                  color: "#e2e8f0",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: "10px 24px",
                  backgroundColor: "#9146FF",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  cursor: saving ? "not-allowed" : "pointer",
                  opacity: saving ? 0.5 : 1,
                }}
              >
                {saving ? "Saving..." : "Update Entry"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Knowledge Analytics Component
  const KnowledgeAnalytics = () => {
    const [analyticsData, setAnalyticsData] = useState<any>(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(true);

    useEffect(() => {
      fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
      setLoadingAnalytics(true);
      try {
        // For now, we'll compute analytics from the entries data
        // In a real implementation, this would be a separate API endpoint
        const totalEntries = entries.length;
        const activeEntries = entries.filter((e) => e.isActive).length;
        const totalUsage = entries.reduce((sum, e) => sum + e.usageCount, 0);
        const mostUsed = [...entries]
          .sort((a, b) => b.usageCount - a.usageCount)
          .slice(0, 10);

        const tagCounts = entries.reduce(
          (acc, e) => {
            e.tags.forEach((tag) => {
              acc[tag] = (acc[tag] || 0) + 1;
            });
            return acc;
          },
          {} as Record<string, number>,
        );

        setAnalyticsData({
          totalEntries,
          activeEntries,
          totalUsage,
          mostUsed,
          tagCounts,
        });
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoadingAnalytics(false);
      }
    };

    if (loadingAnalytics) {
      return (
        <div style={{ textAlign: "center", padding: "40px", color: "#a0aec0" }}>
          Loading analytics...
        </div>
      );
    }

    return (
      <div>
        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(4, 1fr)",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "32px", fontWeight: "bold", color: "#9146FF" }}
            >
              {analyticsData.totalEntries}
            </div>
            <div
              style={{ fontSize: "14px", color: "#cbd5e0", marginTop: "4px" }}
            >
              Total Entries
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "32px", fontWeight: "bold", color: "#10B981" }}
            >
              {analyticsData.activeEntries}
            </div>
            <div
              style={{ fontSize: "14px", color: "#cbd5e0", marginTop: "4px" }}
            >
              Active Entries
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "32px", fontWeight: "bold", color: "#F59E0B" }}
            >
              {analyticsData.totalUsage}
            </div>
            <div
              style={{ fontSize: "14px", color: "#cbd5e0", marginTop: "4px" }}
            >
              Total Usage
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "32px", fontWeight: "bold", color: "#EC4899" }}
            >
              {Object.keys(analyticsData.tagCounts).length}
            </div>
            <div
              style={{ fontSize: "14px", color: "#cbd5e0", marginTop: "4px" }}
            >
              Unique Tags
            </div>
          </div>
        </div>

        {/* Most Used Entries */}
        {analyticsData.mostUsed.length > 0 && (
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "24px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
            }}
          >
            <h3
              style={{
                margin: "0 0 20px 0",
                color: "#e2e8f0",
                fontSize: "18px",
              }}
            >
              <Icon
                name="flame"
                size={18}
                style={{ verticalAlign: "-3px", marginRight: "8px" }}
              />
              Most Used Entries
            </h3>
            <div style={{ display: "grid", gap: "12px" }}>
              {analyticsData.mostUsed
                .filter((e: KnowledgeEntry) => e.usageCount > 0)
                .map((entry: KnowledgeEntry, idx: number) => (
                  <div
                    key={entry.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px",
                      backgroundColor: "#1a202c",
                      borderRadius: "6px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <span
                        style={{
                          width: "24px",
                          height: "24px",
                          backgroundColor:
                            idx === 0
                              ? "#F59E0B"
                              : idx === 1
                                ? "#A78BFA"
                                : idx === 2
                                  ? "#FB923C"
                                  : "#6B7280",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          fontWeight: "bold",
                          color: "#1a202c",
                        }}
                      >
                        {idx + 1}
                      </span>
                      <span style={{ color: "#e2e8f0" }}>{entry.name}</span>
                    </div>
                    <span style={{ color: "#9146FF", fontWeight: "600" }}>
                      {entry.usageCount} uses
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        alignItems: "flex-start",
        gap: "20px",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
      }}
    >
      <div
        style={{
          flex: "1",
          maxWidth: isMobile ? "100%" : "800px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <h2
          style={{ fontSize: "24px", marginBottom: "24px", color: "#e2e8f0" }}
        >
          <Icon
            name="book-open"
            size={22}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Knowledge Base
        </h2>

        {/* Sub-navigation */}
        <div style={subNavStyle}>
          <Link
            to="/dashboard/memorybank/knowledge/items"
            style={subNavLinkStyle(activeSubTab === "items")}
          >
            Knowledge Items
          </Link>
          {entries.length >= maxEntries ? (
            <div
              style={{
                ...subNavLinkStyle(false),
                cursor: "not-allowed",
                opacity: 0.5,
              }}
              onClick={(e) => {
                e.preventDefault();
                if (isPremium) {
                  alert(
                    `You have reached the maximum limit of ${maxEntries} knowledge entries.`,
                  );
                } else {
                  alert(
                    `Free users can only create up to ${maxEntries} knowledge entries. Upgrade to Premium for up to 100 entries!`,
                  );
                }
              }}
            >
              Add Entry
            </div>
          ) : (
            <Link
              to="/dashboard/memorybank/knowledge/add"
              style={subNavLinkStyle(activeSubTab === "add")}
            >
              Add Entry
            </Link>
          )}
          <Link
            to="/dashboard/memorybank/knowledge/analytics"
            style={subNavLinkStyle(activeSubTab === "analytics")}
          >
            Analytics
          </Link>
          <div
            style={{
              marginLeft: "auto",
              color: entries.length >= maxEntries ? "#F59E0B" : "#9146FF",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            {entries.length}/{maxEntries} entries
          </div>
        </div>

        {/* Sub-routes */}
        <Routes>
          <Route path="/" element={<Navigate to="items" replace />} />
          <Route path="items" element={<KnowledgeItems />} />
          <Route path="add" element={<AddKnowledgeEntry />} />
          <Route path="edit/:id" element={<EditKnowledgeEntry />} />
          <Route path="analytics" element={<KnowledgeAnalytics />} />
        </Routes>
      </div>

      {/* Knowledge Base Info Panel */}
      <div
        style={{
          width: isMobile ? "100%" : "350px",
          maxWidth: isMobile ? "800px" : "350px",
          backgroundColor: "rgba(45, 55, 72, 0.95)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          borderRadius: "8px",
          padding: "20px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          height: "fit-content",
          position: "relative",
          order: isMobile ? 2 : 0,
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{
            color: "#e2e8f0",
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "18px",
          }}
        >
          <Icon
            name="book-open"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Knowledge Base Guide
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="brain"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            What is Knowledge Base?
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            A smart information repository that helps your bot provide accurate,
            consistent responses based on verified information you provide.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="sparkles"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Key Features
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>Semantic Search</strong> - AI-powered entry matching
            </li>
            <li>
              <strong>Flexible Tags</strong> - Organize with custom tags
            </li>
            <li>
              <strong>Usage Analytics</strong> - Track what&apos;s most helpful
            </li>
            <li>
              <strong>Attribution Control</strong> - Choose how bot cites
              sources
            </li>
            <li>
              <strong>Real-time Updates</strong> - Changes apply instantly
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="lightbulb"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            How to Use
          </h4>
          <ol
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>Click &ldquo;Add Entry&rdquo; to create knowledge</li>
            <li>Add descriptive name and detailed content</li>
            <li>Tag entries for easy organization</li>
            <li>Set attribution level as needed</li>
            <li>Monitor usage in Analytics tab</li>
          </ol>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="target"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Best Practices
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>Keep entries focused on single topics</li>
            <li>Use clear, descriptive names</li>
            <li>Update information regularly</li>
            <li>Use tags consistently</li>
            <li>Review analytics to optimize</li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="tag"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "6px" }}
            />
            Tagging Tips
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Create tags like: commands, faq, rules, lore, technical, events,
            schedule. Combine multiple tags for better organization.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "12px",
            borderRadius: "6px",
            marginTop: "15px",
            border: "1px solid #4a5568",
          }}
        >
          <p
            style={{
              color: "#9146FF",
              fontSize: "12px",
              margin: "0 0 6px 0",
              fontWeight: "bold",
            }}
          >
            {isPremium ? (
              <>
                <Icon
                  name="star"
                  size={14}
                  style={{ verticalAlign: "-2px", marginRight: "6px" }}
                />
                Premium User
              </>
            ) : (
              "Free Plan"
            )}
          </p>
          <p style={{ color: "#a0aec0", fontSize: "11px", margin: 0 }}>
            {isPremium
              ? `You can create up to ${maxEntries} knowledge entries. Currently using ${entries.length}.`
              : `Free users can create up to ${maxEntries} knowledge entries. Upgrade to Premium for up to 100 entries!`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default React.memo(KnowledgeBaseManager);
